import { useCallback, useRef, useState } from "react";
import { useDataProvider, useNotify } from "ra-core";
import { useFormContext } from "react-hook-form";

import { insertText, usePasteAsMarkdown } from "../misc/usePasteAsMarkdown";
import type { CrmDataProvider } from "../providers/types";
import { imageMarkdown, pastedImages, uploadPlaceholder } from "./inlineImages";

type TextField = HTMLTextAreaElement | HTMLInputElement;

/**
 * Paste for a note's text box: an image lands where the caret is, anything else
 * goes through the HTML-to-markdown paste the box already had. LCCRM-87.
 *
 * The image has to be uploaded before the note is saved, because the text needs
 * its address. A placeholder marks the spot at once and is swapped for the
 * markdown image line when the upload answers; it is found by its text rather
 * than by position, since the user keeps typing in the meantime. The uploaded
 * file also joins the note's `attachments`, so storage cleanup knows about it.
 *
 * Must be used inside the note's form.
 */
export function useNoteImagePaste() {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const { getValues, setValue } = useFormContext();
  const pasteAsMarkdown = usePasteAsMarkdown();
  const [uploading, setUploading] = useState(0);
  // The same count, readable from a validator without re-creating it.
  const pending = useRef(0);
  const sequence = useRef(0);

  const upload = useCallback(
    async (element: TextField, file: File) => {
      const placeholder = uploadPlaceholder((sequence.current += 1));
      insertText(element, placeholder);
      pending.current += 1;
      setUploading(pending.current);
      const swap = (replacement: string) => {
        const text: string = getValues("text") ?? "";
        // Gone when the user deleted it, or the form was reset by a save.
        if (!text.includes(placeholder)) return false;
        setValue("text", text.replace(placeholder, replacement), {
          shouldDirty: true,
        });
        return true;
      };
      try {
        const uploaded = await dataProvider.uploadNoteImage(file);
        if (swap(imageMarkdown(uploaded.src))) {
          setValue(
            "attachments",
            [...(getValues("attachments") ?? []), uploaded],
            { shouldDirty: true },
          );
        }
      } catch {
        swap("");
        notify("resources.notes.inputs.image_upload_failed", {
          type: "error",
          messageArgs: {
            _: "Couldn't upload the image. Try pasting it again.",
          },
        });
      } finally {
        pending.current -= 1;
        setUploading(pending.current);
      }
    },
    [dataProvider, getValues, notify, setValue],
  );

  const onPaste = useCallback(
    (event: React.ClipboardEvent<TextField>) => {
      const images = pastedImages(event.clipboardData);
      if (images.length === 0) {
        pasteAsMarkdown(event);
        return;
      }
      event.preventDefault();
      const element = event.currentTarget;
      for (const file of images) void upload(element, file);
    },
    [pasteAsMarkdown, upload],
  );

  /** A validator for the text input: no saving a note mid-upload. */
  const validateNotUploading = useCallback(
    () =>
      pending.current > 0
        ? "resources.notes.inputs.image_uploading_wait"
        : undefined,
    [],
  );

  return { onPaste, uploading: uploading > 0, validateNotUploading };
}
